"use client";

// Lightweight toast + global banner system. Three pieces:
//
// 1. <NotifyProvider>            — wraps the app, owns the toast queue
// 2. useNotify()                 — components call this to push toasts
// 3. NetworkStatusBanner          — sticky banner that reads navigator.onLine
//
// The api.ts onAuthLost callback gets wired to this provider via
// `setNotifier()` so a 401 in any component surfaces a toast.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import clsx from "clsx";

type Level = "info" | "success" | "warning" | "danger";
interface Toast {
  id: string;
  level: Level;
  message: string;
  ttl: number;
}

interface NotifyValue {
  notify: (level: Level, message: string, ttl?: number) => void;
  dismiss: (id: string) => void;
}

const NotifyContext = createContext<NotifyValue | null>(null);

// Module-level pointer so non-React modules (api.ts) can push toasts.
let _push: ((level: Level, message: string, ttl?: number) => void) | null = null;
export function setNotifier(fn: typeof _push) { _push = fn; }
export function pushToast(level: Level, message: string, ttl?: number) {
  _push?.(level, message, ttl);
}

export function NotifyProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback((level: Level, message: string, ttl = 5000) => {
    const id = `t${++idRef.current}_${Date.now()}`;
    setToasts((prev) => [...prev, { id, level, message, ttl }]);
    if (ttl > 0) setTimeout(() => dismiss(id), ttl);
  }, [dismiss]);

  useEffect(() => {
    setNotifier(notify);
    return () => setNotifier(null);
  }, [notify]);

  return (
    <NotifyContext.Provider value={{ notify, dismiss }}>
      {children}
      <NetworkStatusBanner />
      <div style={{
        position: "fixed", bottom: 16, right: 16, display: "flex",
        flexDirection: "column", gap: 8, zIndex: 100, maxWidth: 360,
      }}>
        {toasts.map((t) => (
          <div key={t.id}
               className={clsx("card", `toast-${t.level}`)}
               style={{
                 padding: "10px 14px",
                 boxShadow: "var(--shadow-lg)",
                 borderLeftWidth: 4,
                 borderLeftStyle: "solid",
                 borderLeftColor: borderFor(t.level),
                 fontSize: 12.5,
                 display: "flex", alignItems: "center", gap: 10,
               }}>
            <i className={iconFor(t.level)} style={{ color: borderFor(t.level) }} />
            <span style={{ flex: 1 }}>{t.message}</span>
            <button onClick={() => dismiss(t.id)} className="icon-btn" style={{ width: 24, height: 24 }}>
              <i className="bi bi-x" />
            </button>
          </div>
        ))}
      </div>
    </NotifyContext.Provider>
  );
}

export function useNotify(): NotifyValue {
  const ctx = useContext(NotifyContext);
  if (!ctx) throw new Error("useNotify must be inside <NotifyProvider>");
  return ctx;
}

function borderFor(l: Level) {
  return l === "success" ? "var(--success)"
       : l === "warning" ? "var(--warning)"
       : l === "danger"  ? "var(--danger)"
       : "var(--info)";
}
function iconFor(l: Level) {
  return l === "success" ? "bi bi-check-circle-fill"
       : l === "warning" ? "bi bi-exclamation-triangle-fill"
       : l === "danger"  ? "bi bi-x-circle-fill"
       : "bi bi-info-circle-fill";
}

// ─── Network status banner ───────────────────────────────────────────────

function NetworkStatusBanner() {
  const [online, setOnline] = useState(true);

  useEffect(() => {
    if (typeof navigator !== "undefined") setOnline(navigator.onLine);
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  if (online) return null;
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 99,
      background: "var(--danger-soft)", color: "var(--danger)",
      padding: "8px 16px", fontSize: 12, textAlign: "center",
      borderBottom: "1px solid var(--danger)",
    }}>
      <i className="bi bi-wifi-off" /> ขาดการเชื่อมต่อเครือข่าย — กำลังพยายามเชื่อมต่อใหม่
    </div>
  );
}
